import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CampaignPreview = ({ campaignData, emailContent }) => {
  const [previewMode, setPreviewMode] = useState('desktop');
  const [selectedContact, setSelectedContact] = useState('sarah-mitchell');

  const previewModes = [
    { value: 'desktop', label: 'Desktop', icon: 'Monitor' },
    { value: 'mobile', label: 'Mobile', icon: 'Smartphone' },
    { value: 'tablet', label: 'Tablet', icon: 'Tablet' }
  ];

  const mockContacts = [
    { value: 'sarah-mitchell', label: 'Sarah Mitchell - Literary Review Weekly' },
    { value: 'marcus-rodriguez', label: 'Marcus Rodriguez - The Literary Times' },
    { value: 'emily-chen', label: 'Emily Chen - Book Blog Central' }
  ];

  const contactData = {
    'sarah-mitchell': {
      name: 'Sarah Mitchell',
      outlet: 'Literary Review Weekly',
      bookTitle: 'The Midnight Garden',
      authorName: 'Isabella Hartwell',
      bookGenre: 'Gothic Romance',
      mainTheme: 'love transcending time and death',
      publicationDate: 'November 15, 2024',
      senderName: 'Jennifer Walsh',
      senderTitle: 'PR Manager'
    },
    'marcus-rodriguez': {
      name: 'Marcus Rodriguez',
      outlet: 'The Literary Times',
      bookTitle: 'Silicon Dreams',
      authorName: 'Dr. Michael Chen',
      bookGenre: 'Science Fiction',
      mainTheme: 'artificial intelligence and human consciousness',
      publicationDate: 'December 1, 2024',
      senderName: 'Jennifer Walsh',
      senderTitle: 'PR Manager'
    },
    'emily-chen': {
      name: 'Emily Chen',
      outlet: 'Book Blog Central',
      bookTitle: 'Hearts in Harmony',
      authorName: 'Sophie Williams',
      bookGenre: 'Contemporary Romance',
      mainTheme: 'second chances and musical passion',
      publicationDate: 'November 22, 2024',
      senderName: 'Jennifer Walsh',
      senderTitle: 'PR Manager'
    }
  };

  const personalizeContent = (content, contact) => {
    const data = contactData?.[contact];
    if (!data) return content;

    return content?.replace(/\{\{contact_name\}\}/g, data?.name)?.replace(/\{\{contact_outlet\}\}/g, data?.outlet)?.replace(/\{\{book_title\}\}/g, data?.bookTitle)?.replace(/\{\{author_name\}\}/g, data?.authorName)?.replace(/\{\{book_genre\}\}/g, data?.bookGenre)?.replace(/\{\{main_theme\}\}/g, data?.mainTheme)?.replace(/\{\{publication_date\}\}/g, data?.publicationDate)?.replace(/\{\{sender_name\}\}/g, data?.senderName)?.replace(/\{\{sender_title\}\}/g, data?.senderTitle);
  };

  const getSpamScore = () => {
    const content = emailContent || '';
    let score = 0;
    
    // Simple spam score calculation
    if (content?.includes('!!!')) score += 2;
    if (content?.toUpperCase() === content) score += 3;
    if (content?.includes('FREE') || content?.includes('URGENT')) score += 1;
    if (content?.length < 50) score += 2;
    
    return Math.min(score, 10);
  };

  const getDeliverabilityRating = () => {
    const spamScore = getSpamScore();
    if (spamScore <= 2) return { rating: 'Excellent', color: 'text-success', percentage: 95 };
    if (spamScore <= 4) return { rating: 'Good', color: 'text-accent', percentage: 85 };
    if (spamScore <= 6) return { rating: 'Fair', color: 'text-warning', percentage: 70 };
    return { rating: 'Poor', color: 'text-error', percentage: 45 };
  };

  const deliverability = getDeliverabilityRating();
  const personalizedContent = personalizeContent(emailContent || '', selectedContact);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-success rounded-md flex items-center justify-center">
              <Icon name="Eye" size={16} color="white" />
            </div>
            <h2 className="text-lg font-heading font-semibold text-foreground">Campaign Preview</h2>
          </div>
          <div className="flex items-center space-x-2">
            {previewModes?.map((mode) => (
              <button
                key={mode?.value}
                onClick={() => setPreviewMode(mode?.value)}
                className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm transition-colors ${
                  previewMode === mode?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={mode?.icon} size={14} />
                <span>{mode?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Contact Selection */}
        <Select
          label="Preview for Contact"
          options={mockContacts}
          value={selectedContact}
          onChange={setSelectedContact}
        />
      </div>
      {/* Preview Content */}
      <div className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Email Preview */}
          <div className="lg:col-span-2">
            <div className={`border border-border rounded-lg overflow-hidden ${
              previewMode === 'mobile' ? 'max-w-sm mx-auto' : 
              previewMode === 'tablet' ? 'max-w-md mx-auto' : 'w-full'
            }`}>
              {/* Email Header */}
              <div className="bg-muted p-4 border-b border-border">
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-foreground">From: jennifer.walsh@pengamepublishing.com</div>
                    <div className="text-muted-foreground">To: {contactData?.[selectedContact]?.name?.toLowerCase()?.replace(' ', '.')}@{contactData?.[selectedContact]?.outlet?.toLowerCase()?.replace(/\s+/g, '')}.com</div>
                  </div>
                  <div className="text-muted-foreground">
                    {new Date()?.toLocaleDateString()}
                  </div>
                </div>
                <div className="mt-2">
                  <div className="font-medium text-foreground">
                    Subject: {campaignData?.subject || 'Your Email Subject Here'}
                  </div>
                </div>
              </div>

              {/* Email Body */}
              <div className="p-4 bg-white min-h-64">
                <div className="prose prose-sm max-w-none">
                  {personalizedContent?.split('\n')?.map((paragraph, index) => (
                    <p key={index} className="mb-3 text-foreground leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Attachments Preview */}
                <div className="mt-6 pt-4 border-t border-border">
                  <div className="text-sm font-medium text-foreground mb-2">Attachments (3)</div>
                  <div className="space-y-2">
                    {[
                      { name: 'Press_Kit_Complete.pdf', size: '2.4 MB' },
                      { name: 'Author_Headshot_HighRes.jpg', size: '1.8 MB' },
                      { name: 'Book_Cover_300DPI.png', size: '3.2 MB' }
                    ]?.map((file, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Paperclip" size={12} />
                        <span>{file?.name}</span>
                        <span>({file?.size})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Analysis Panel */}
          <div className="space-y-6">
            {/* Deliverability Score */}
            <div className="bg-muted rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Deliverability Analysis</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Spam Score</span>
                  <span className="text-sm font-medium text-foreground">{getSpamScore()}/10</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Deliverability</span>
                  <span className={`text-sm font-medium ${deliverability?.color}`}>
                    {deliverability?.rating}
                  </span>
                </div>
                <div className="w-full bg-border rounded-full h-2">
                  <div 
                    className="bg-success h-2 rounded-full transition-all duration-300"
                    style={{ width: `${deliverability?.percentage}%` }}
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  {deliverability?.percentage}% estimated inbox delivery rate
                </div>
              </div>
            </div>

            {/* Content Analysis */}
            <div className="bg-muted rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Content Analysis</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Word Count</span>
                  <span className="text-foreground">{personalizedContent?.split(' ')?.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reading Time</span>
                  <span className="text-foreground">{Math.ceil(personalizedContent?.split(' ')?.length / 200)} min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Personalization</span>
                  <span className="text-success">High</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Links</span>
                  <span className="text-foreground">0</span>
                </div>
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-muted rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Recommendations</h3>
              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
                  <span className="text-xs text-muted-foreground">Good personalization usage</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
                  <span className="text-xs text-muted-foreground">Professional tone maintained</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="AlertCircle" size={14} className="text-warning mt-0.5" />
                  <span className="text-xs text-muted-foreground">Consider adding a clear call-to-action</span>
                </div>
              </div>
            </div>

            {/* Send Test Email */}
            <Button variant="outline" fullWidth iconName="Send" iconPosition="left">
              Send Test Email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignPreview;